import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { authService, type User } from '../services/auth.service';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, senha: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Provider de Autenticação
 * AIDEV-NOTE: Gerencia estado de auth em toda a aplicação
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Verifica sessão ao montar
    authService
      .getCurrentUser()
      .then((currentUser) => {
        setUser(currentUser);
      })
      .catch(() => {
        setUser(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const login = async (email: string, senha: string) => {
    const response = await authService.login(email, senha);
    setUser(response.usuario);
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };

  // AIDEV-NOTE: Só atualiza o user se a resposta for válida — nunca chama setUser(null).
  // Se /auth/me falhar (401 transitório, restart de servidor, etc.) o usuário permanece
  // logado com as políticas anteriores; um erro de auth real será capturado pelo interceptor
  // na próxima request relevante, que fará o redirect para login de forma controlada.
  const refreshUser = async () => {
    const currentUser = await authService.getCurrentUser();
    if (currentUser !== null) {
      setUser(currentUser);
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    refreshUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook de autenticação
 */
export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }

  return context;
}
